import { useMemo } from 'react';

import { IUserMinimalOrderFragment, useGetUserMinimalOrderQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import logger from 'utils/logger';

import { mapMinimalOrderToAccountOrder } from './minimal-order-utils';
import { MinimalAccountOrder } from './types';

export const useMinimalAccountOrderDetails = (rbiOrderId?: string | null) => {
  const { isAuthenticated } = useAuthContext();
  const {
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  const { data, error, loading, refetch } = useGetUserMinimalOrderQuery({
    variables: {
      id: rbiOrderId!,
    },
    skip: rbiOrderId == null || !isAuthenticated,
    fetchPolicy: 'no-cache',
  });

  const order = useMemo<MinimalAccountOrder | undefined>(() => {
    if (!data?.minimalOrder || isUnifiedMenuLoading) {
      return undefined;
    }

    try {
      return mapMinimalOrderToAccountOrder({
        order: data.minimalOrder as IUserMinimalOrderFragment,
        menu: indexedMenu,
        indexedOffers,
        indexedRewards,
        posServiceMode,
      });
    } catch (mappingError) {
      logger.warn({
        message: '[useMinimalAccountOrderDetails] Failed to map minimal account order',
        context: {
          rbiOrderId: data.minimalOrder.rbiOrderId,
          orderNumberFormatted: data.minimalOrder.orderNumberFormatted,
          menuSelectionCount: data.minimalOrder.cart.menuSelections?.length ?? 0,
        },
        error: mappingError,
      });
      return undefined;
    }
  }, [
    data?.minimalOrder,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isUnifiedMenuLoading,
    posServiceMode,
  ]);

  return {
    error,
    loading: loading || isUnifiedMenuLoading,
    order,
    refetch,
  };
};
